"use client";
import {
  LayoutContext,
  LayoutContextProvider
} from "./chunk-FE37TETR.js";
import {
  Nav,
  ThemeToggle
} from "./chunk-JHNPSDZB.js";
import {
  useSidebarCollapse
} from "./chunk-WTO3J3EA.js";
import "./chunk-3P4ASMQP.js";
import {
  ScrollArea
} from "./chunk-CIPMRU3J.js";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "./chunk-JRILMVZA.js";
import {
  cn
} from "./chunk-S2JEC2ZU.js";
import "./chunk-6C3VEZWH.js";

// src/components/sidebar.tsx
import { cva } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import * as Base from "next-docs-zeta/sidebar";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var itemVariants = cva(
  "flex flex-row items-center gap-2 rounded-md px-2 py-1.5 font-medium text-muted-foreground [&_svg]:h-4 [&_svg]:w-4",
  {
    variants: {
      active: {
        true: "bg-primary/10 text-primary",
        false: "hover:bg-accent/50"
      }
    }
  }
);
function Sidebar({ banner, footer }) {
  const [open] = useSidebarCollapse();
  const { tree } = useContext(LayoutContext);
  return /* @__PURE__ */ jsxs(
    Base.SidebarList,
    {
      minWidth: 768,
      className: cn(
        "flex w-full flex-col text-medium md:sticky md:top-16 md:h-body md:w-[240px] md:text-sm xl:w-[260px]",
        !open && "hidden",
        "max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-40 max-md:bg-background max-md:pt-16 max-md:data-[open=false]:hidden sm:max-md:max-w-sm sm:max-md:border-l"
      ),
      children: [
        /* @__PURE__ */ jsx(ScrollArea, { className: "flex-1", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col pb-10 pt-4 max-md:px-4 md:pr-4 md:pt-10", children: [
          banner,
          tree.children.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key -- tree nodes have no id
            /* @__PURE__ */ jsx(Node, { item, level: 1 }, i)
          ))
        ] }) }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "flex flex-row items-center gap-2 border-t py-2 max-md:px-4",
              !footer && "md:hidden"
            ),
            children: [
              footer,
              /* @__PURE__ */ jsx(ThemeToggle, { className: "md:hidden" })
            ]
          }
        )
      ]
    }
  );
}
function Node({ item, level }) {
  if (item.type === "separator")
    return /* @__PURE__ */ jsx("p", { className: "mb-2 mt-8 px-2 font-medium first:mt-0", children: item.name });
  if (item.type === "folder")
    return /* @__PURE__ */ jsx(Folder, { item, level });
  return /* @__PURE__ */ jsx(Item, { item });
}
function Item({ item }) {
  const pathname = usePathname();
  const active = pathname === item.url;
  return /* @__PURE__ */ jsxs(Link, { href: item.url, className: cn(itemVariants({ active })), children: [
    item.icon,
    item.name
  ] });
}
function hasActive(items, url) {
  return items.some((item) => {
    if (item.type === "page") {
      return item.url === url;
    }
    if (item.type === "folder")
      return hasActive(item.children, url);
    return false;
  });
}
function Folder({
  item: { name, children, index, icon },
  level
}) {
  const { sidebarDefaultOpenLevel = 1 } = useContext(LayoutContext);
  const pathname = usePathname();
  const active = index && pathname === index.url;
  const childActive = useMemo(
    () => hasActive(children, pathname),
    [children, pathname]
  );
  const [extend, setExtend] = useState(
    active || childActive || sidebarDefaultOpenLevel >= level
  );
  useEffect(() => {
    if (active || childActive)
      setExtend(true);
  }, [active, childActive]);
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    icon,
    name,
    /* @__PURE__ */ jsx(
      ChevronDown,
      {
        onClick: (e) => {
          setExtend((prev) => !prev);
          e.preventDefault();
        },
        className: cn("ml-auto transition-transform", !extend && "-rotate-90")
      }
    )
  ] });
  return /* @__PURE__ */ jsxs(
    Collapsible,
    {
      open: extend,
      onOpenChange: !index || active ? setExtend : void 0,
      children: [
        /* @__PURE__ */ jsx(
          CollapsibleTrigger,
          {
            className: cn(itemVariants({ active, className: "w-full" })),
            asChild: true,
            children: index ? /* @__PURE__ */ jsx(Link, { href: index.url, children: content }) : /* @__PURE__ */ jsx("button", { type: "button", children: content })
          }
        ),
        /* @__PURE__ */ jsx(CollapsibleContent, { children: /* @__PURE__ */ jsx("div", { className: "ml-4 flex flex-col border-l py-2 pl-2", children: children.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key -- tree nodes have no id
          /* @__PURE__ */ jsx(Node, { item, level: level + 1 }, i)
        )) }) })
      ]
    }
  );
}
export {
  LayoutContextProvider,
  Nav,
  Sidebar
};
